import { InlineLoading, Layer } from "@carbon/react";
import { TextError } from "@flaminem/ui-components";
import React from "react";

import { InlineNotification } from "src/components/Notifications/InlineNotification";
import { useGetBlacklistedIdentifiers } from "src/graphql/anti-fraud/queries/cases/GetCaseBlacklistedIdentifiersQuery.ts";
import { useT } from "src/internationalization";
import { BlacklistableCaseIdentifier } from "src/types/case";

import { BlacklistableIdentifiersTable } from "./BlacklistableIdentifiersTable";
import { FormEmptyView } from "./FormEmptyView";
import { BlacklistIdentifiersFormControl } from "./useBlacklistIdentifiersForm";

export type BlacklistIdentifiersFormProps = {
  caseId: string;
  blacklistIdentifiersFormControl: BlacklistIdentifiersFormControl;
};

export const BlacklistIdentifiersForm: React.FC<BlacklistIdentifiersFormProps> = ({
  caseId,
  blacklistIdentifiersFormControl,
}) => {
  const t = useT();

  const {
    blacklistableIdentifiers: {
      data: blacklistableIdentifiers,
      loading: loadingBlacklistableIdentifiers,
      error: errorBlacklistableIdentifiers,
    },
  } = blacklistIdentifiersFormControl;

  const {
    data: blacklistedIdentifiers,
    loading: loadingBlacklistedIdentifiers,
    error: errorBlacklistedIdentifiers,
  } = useGetBlacklistedIdentifiers({
    variables: {
      id: caseId,
    },
  });

  const loading = loadingBlacklistableIdentifiers || loadingBlacklistedIdentifiers;
  const error = errorBlacklistableIdentifiers || errorBlacklistedIdentifiers;

  const handleSelect = (newSelectedIdentifiers: BlacklistableCaseIdentifier[]) => {
    blacklistIdentifiersFormControl.methods.setValue(
      "selectedIdentifiers",
      newSelectedIdentifiers.map(({ id, identifier, element, blacklist }) => ({
        id,
        identifierCode: identifier.schemaIdentifier.code,
        elementCode: element.typeCode,
        blacklistCode: blacklist.code,
      }))
    );

    if (blacklistIdentifiersFormControl.methods.formState.isSubmitted) {
      blacklistIdentifiersFormControl.methods.trigger();
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full justify-center items-center">
        <div>
          <InlineLoading />
        </div>
      </div>
    );
  }

  if (error || !blacklistableIdentifiers || !blacklistedIdentifiers) {
    return (
      <div className="p-5">
        <TextError message={error?.message ?? ""} />
      </div>
    );
  }

  if (blacklistableIdentifiers.length === 0) {
    return <FormEmptyView tooltip={{ blacklistedIdentifiers: blacklistedIdentifiers }} />;
  }

  return (
    <Layer className="flex flex-col h-full gap-5" level={0}>
      {blacklistIdentifiersFormControl.methods.formState.errors.selectedIdentifiers && (
        <div className="flex flex-col px-5">
          <InlineNotification
            kind="error"
            title={t("general.error.validation.title")}
            subtitle={
              blacklistIdentifiersFormControl.methods.formState.errors.selectedIdentifiers.message
            }
            fullWidth
          />
        </div>
      )}
      <BlacklistableIdentifiersTable
        caseId={caseId}
        blacklistableIdentifiers={blacklistableIdentifiers}
        handleSelect={handleSelect}
      />
    </Layer>
  );
};
